import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { buildUpcomingDays, toISODate, mondayIndex, WEEKDAYS_FR, MONTHS_FR, formatDateFR } from "@/lib/dates";
import { Snowflake, Clock, Car, Phone, Calendar as CalIcon, Loader2, X, RefreshCw, ArrowLeft, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const statusMap = {
  confirmed: { label: "Confirmée", cls: "bg-[#06D6A0]/15 text-[#06D6A0] border-[#06D6A0]/30" },
  cancelled: { label: "Annulée", cls: "bg-[#EF476F]/15 text-[#EF476F] border-[#EF476F]/30" },
  done: { label: "Terminée", cls: "bg-[#00B4D8]/15 text-[#00B4D8] border-[#00B4D8]/30" },
};

export default function Manage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [reschService, setReschService] = useState(null);
  const [days, setDays] = useState([]);
  const [selDate, setSelDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selSlot, setSelSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/manage/${token}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const cancel = async (id) => {
    if (!window.confirm("Annuler définitivement cette réservation ?")) return;
    try {
      await api.post(`/manage/${token}/cancel/${id}`);
      toast.success("Réservation annulée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openReschedule = async (b) => {
    setRescheduleId(b.id); setSelDate(null); setSelSlot(null); setSlots([]);
    setReschService({ id: b.service_id });
    setDays(buildUpcomingDays(28).slice(0, 14));
  };

  const loadSlots = async (iso) => {
    setSelDate(iso); setSelSlot(null); setSlots([]); setBusy(true);
    try {
      const r = await api.get(`/availability`, { params: { service_id: reschService.id, date: iso } });
      setSlots(r.data.slots);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmReschedule = async () => {
    setBusy(true);
    try {
      await api.post(`/manage/${token}/reschedule/${rescheduleId}`, { date: selDate, start_time: selSlot });
      toast.success("Rendez-vous décalé");
      setRescheduleId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#050810]"><Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" /></div>;
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#050810] px-6 text-center">
      <Ban className="w-12 h-12 text-[#EF476F] mb-4" />
      <p className="text-white text-lg font-semibold mb-2">{error}</p>
      <a href="/" className="text-[#00B4D8] hover:underline mt-2">Retour à l'accueil</a>
    </div>
  );

  const upcoming = data.bookings.filter((b) => b.status === "confirmed");
  const past = data.bookings.filter((b) => b.status !== "confirmed");

  return (
    <div className="min-h-screen bg-[#050810] text-white">
      <nav className="glass border-b border-[#1A2642]">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center"><Logo className="h-9 w-auto" /></a>
          <a href="/" className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Accueil</a>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="font-heading text-3xl font-bold mb-1">Mes réservations</h1>
        <p className="text-[#94A3B8] mb-8">{data.customer.name} · {data.customer.email}</p>

        {upcoming.length === 0 && <p className="text-[#94A3B8]">Aucune réservation à venir.</p>}

        <div className="space-y-4">
          {upcoming.map((b) => (
            <div key={b.id} className="glass border border-[#1A2642] rounded-lg p-5" data-testid={`booking-${b.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${statusMap[b.status].cls}`}>{statusMap[b.status].label}</span>
                  <h3 className="font-heading font-bold text-lg mt-2">{b.service_name}</h3>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-[#94A3B8]">
                    <span className="flex items-center gap-1.5"><CalIcon className="w-4 h-4 text-[#00B4D8]" />{formatDateFR(b.date)}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#00B4D8]" />{b.start_time} - {b.end_time}</span>
                    <span className="flex items-center gap-1.5"><Car className="w-4 h-4 text-[#00B4D8]" />{b.vehicle_marque} {b.vehicle_modele} ({b.vehicle_annee})</span>
                  </div>
                </div>
                <span className="font-heading text-2xl font-black text-[#FFD60A] shrink-0">{b.service_price} €</span>
              </div>

              {rescheduleId === b.id ? (
                <div className="mt-5 pt-5 border-t border-[#1A2642]" data-testid="reschedule-panel">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#94A3B8] mb-2">Nouvelle date</p>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {days.map((d) => {
                      const iso = toISODate(d); const active = selDate === iso;
                      return (
                        <button key={iso} onClick={() => loadSlots(iso)} data-testid={`resch-day-${iso}`}
                          className={`shrink-0 w-14 py-2 rounded-md border text-center text-xs ${active ? "bg-[#00B4D8] border-[#00B4D8] text-[#050810]" : "bg-[#050810] border-[#1A2642] hover:border-[#00B4D8]"}`}>
                          <div className="font-bold">{WEEKDAYS_FR[mondayIndex(d)]}</div>
                          <div className="text-lg font-bold">{d.getDate()}</div>
                          <div>{MONTHS_FR[d.getMonth()]}</div>
                        </button>
                      );
                    })}
                  </div>
                  {selDate && (busy ? <Loader2 className="w-5 h-5 animate-spin my-3 text-[#00B4D8]" /> : (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-3">
                      {slots.length === 0 ? <p className="text-sm text-[#94A3B8] col-span-full">Aucun créneau ce jour.</p> :
                        slots.map((s) => (
                          <button key={s} onClick={() => setSelSlot(s)} data-testid={`resch-slot-${s}`}
                            className={`py-2 rounded-md border text-sm font-semibold ${selSlot === s ? "bg-[#FFD60A] border-[#FFD60A] text-[#050810]" : "bg-[#050810] border-[#1A2642] hover:border-[#00B4D8]"}`}>{s}</button>
                        ))}
                    </div>
                  ))}
                  <div className="flex gap-2 mt-4">
                    <button disabled={!selSlot || busy} onClick={confirmReschedule} data-testid="confirm-reschedule-btn"
                      className="bg-[#00B4D8] text-[#050810] font-bold rounded-full px-5 py-2.5 text-sm disabled:opacity-40 hover:bg-[#90E0EF] transition flex items-center gap-2">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Valider
                    </button>
                    <button onClick={() => setRescheduleId(null)} className="border border-[#1A2642] rounded-full px-5 py-2.5 text-sm hover:border-white transition">Annuler</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-5 pt-4 border-t border-[#1A2642]">
                  <button onClick={() => openReschedule(b)} data-testid={`reschedule-btn-${b.id}`}
                    className="flex items-center gap-1.5 text-sm font-semibold border border-[#1A2642] rounded-full px-4 py-2 hover:border-[#00B4D8] transition"><RefreshCw className="w-4 h-4" /> Décaler</button>
                  <button onClick={() => cancel(b.id)} data-testid={`cancel-btn-${b.id}`}
                    className="flex items-center gap-1.5 text-sm font-semibold text-[#EF476F] border border-[#EF476F]/30 rounded-full px-4 py-2 hover:bg-[#EF476F]/10 transition"><X className="w-4 h-4" /> Annuler</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {past.length > 0 && (
          <div className="mt-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8] mb-4">Historique</p>
            <div className="space-y-3">
              {past.map((b) => (
                <div key={b.id} className="flex items-center justify-between border border-[#1A2642] rounded-lg p-4 opacity-70">
                  <div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${statusMap[b.status].cls}`}>{statusMap[b.status].label}</span>
                    <p className="text-sm mt-1.5">{b.service_name} · {formatDateFR(b.date)} · {b.start_time}</p>
                  </div>
                  <span className="text-[#FFD60A] font-bold">{b.service_price} €</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
